import React, { useEffect, useState } from 'react'
import { Nav, Tab } from 'react-bootstrap'
import { connect } from 'react-redux'
import { TRANSACTION_FAILED, TRANSACTION_PROCESSING, transactionInit } from '../../../Pricing/actions'
import { LoadingBox } from '../../LoadingBox/LoadingBox'
import { ErrorBox } from '../../ErrorBox/ErrorBox'
import { ERROR_CODE_MAP } from '../../../stripe.constants'
import StripePay from '../../PaymentModal/components/Stripe/StripePay'
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3'
import CurrencyDropdown from '../../CurrencyDropdown/CurrencyDropDown'
import { availableCurrencies } from '../../../constants/currency'
import CustomModal from '../../CustomModal/CustomModal'
import { onUpgradePlan } from '../../../utils/general'
import { PLANS } from '../../../constants/plans'

import styles from './FreeTrialPaymentModal.module.css'

const getErrorMessagePayment = ({ declineCode = '', errorCode, accountAlreadyExistsMessage }) => {
  if (accountAlreadyExistsMessage) {
    return accountAlreadyExistsMessage
  }

  // Try to find error message by errorCode first
  if (errorCode) {
    const errorCodeLower = errorCode.toLowerCase()
    if (ERROR_CODE_MAP[errorCodeLower]) {
      return ERROR_CODE_MAP[errorCodeLower]
    }
  }

  // Try to find error message by declineCode
  if (declineCode) {
    const declineCodeLower = declineCode.toLowerCase()
    if (ERROR_CODE_MAP[declineCodeLower]) {
      return ERROR_CODE_MAP[declineCodeLower]
    }
  }

  // If we have a specific error code but no mapping, provide a generic card error message
  if (errorCode && (errorCode.includes('card') || errorCode.includes('payment') || errorCode.includes('declined'))) {
    return 'Your card was declined. Please check your card details and try again, or use a different card.'
  }

  // Fallback to generic error message
  return 'Sorry, an error occurred processing your transaction. Please check your card details and try again.'
}

const FreeTrialPaymentModal = ({
  show,
  onHide,
  dispatch,
  declineCode,
  errorCode,
  transactionState,
  email,
  studentData,
  plan,
  periodProp = 'monthly',
  source,
  accountAlreadyExistsMessage,
  accountCreated,
  onCurrencySelected,
  onFreeTrialSuccess,
  allowB2C,
  userProfile,
  planType,
  hideClose = false
}) => {
  const [period, setPeriod] = useState(periodProp)
  const isLoading = transactionState === TRANSACTION_PROCESSING
  const isError = transactionState === TRANSACTION_FAILED
  const priceData = plan?.prices?.[period] || {}
  const [errorMessage, setErrorMessage] = useState('')
  const [discountPercent, setDiscountPercent] = useState()

  // Don't render if plan is not available
  if (!plan) {
    return null
  }

  const timeline = [
    {
      icon: '/assets/icons/lock-open.svg',
      title: 'Today',
      description: `Unlock all features of the ${allowB2C ? 'Solo' : 'Pro'} plan`,
      isActive: true
    },
    {
      icon: '/assets/icons/bell-alert.svg',
      title: 'Day 5',
      description: 'Get a reminder that your trial is about to end'
    },
    {
      icon: '/assets/icons/logo_icon_dark.svg',
      title: 'Day 7',
      description: 'Your subscription starts. Cancel anytime.'
    }
  ]

  useEffect(() => setPeriod(periodProp), [periodProp])

  const handleCouponCodeChange = (e) => {
    setDiscountPercent(e)
  }

  const onClose = () => {
    onHide()
  }

  const onTryAgain = ({ dispatch, accountAlreadyExistsMessage }) => {
    if (accountAlreadyExistsMessage) return () => (window.location.href = '/login')
    return () => dispatch(transactionInit())
  }

  const handleUpgradeClick = () => {
    // Reset the transaction state to clear the error
    dispatch(transactionInit())

    // Close the free trial modal
    onHide()

    // Determine which plan to show based on planType
    // planType is 'solo' for B2C or 'pro' for B2B
    const upgradePlanId = planType === 'solo' ? PLANS.PRO.id : PLANS.PRO_U.id

    // Show the upgrade modal
    onUpgradePlan({ planId: upgradePlanId, userProfile }, dispatch)
  }

  const isTrialAlreadyUsedError = errorCode?.toLowerCase() === 'trial_already_used'

  useEffect(() => {
    if (show) {
      dispatch(transactionInit())
    }
  }, [show, dispatch])

  return (
    <CustomModal
      show={show}
      onHide={onClose}
      size="m"
      className={styles.modal}
      centered
      enforceFocus={false}
      mobileCloseButton={false}
      backdrop={hideClose ? 'static' : true}
      keyboard={!hideClose}
    >
      <div className={styles.paymentContainer}>
        <div className={styles.header}>
          <span className={styles.heading}>7 Day free trial</span>
          {!hideClose && (
            <button className={styles.closeButton} onClick={onHide}>
              <img src="/assets/icons/cancel_black.svg" height="15" width="15" alt="cancel" />
            </button>
          )}
        </div>

        <div className={styles.timelineContainer}>
          <div className={styles.timeline}>
            {timeline.map((item, index) => (
              <div key={index} className={`${styles.timelineItem} ${item.isActive ? styles.active : ''}`}>
                <div className={styles.timelineIcon}>
                  <img src={item.icon} alt="" height="16" width="16" />
                </div>
                <div className={styles.timelineContent}>
                  <h4 className={styles.timelineTitle}>{item.title}</h4>
                  <p className={styles.timelineDescription}>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.totalContainer}>
          <div className={styles.totalContainerContent}>
            <span>Billed</span>
            <p>
              <span className="font-weight-bold">{`${plan?.prices?.monthly?.symbol || '$'}`}0</span>
              <CurrencyDropdown
                selectedCurrency={plan.prices.monthly?.symbol}
                onCurrencySelected={onCurrencySelected}
                availableCurrencies={availableCurrencies}
                showSymbol={false}
              />
            </p>
          </div>
        </div>

        <div className="mt-3 mb-3">
          <>
            <Tab.Container defaultActiveKey="card">
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="card">Credit Card</Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="card">
                  <StripePay
                    className={isLoading || isError ? 'd-none' : 'd-block mt-3'}
                    dispatch={dispatch}
                    priceData={priceData}
                    email={email}
                    studentData={{}}
                    source={source}
                    productId={plan.id}
                    period={period}
                    accountCreated={accountCreated}
                    handleCouponCodeChange={handleCouponCodeChange}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    isFreeTrail={true}
                    onFreeTrialSuccess={onFreeTrialSuccess}
                    showUpgradeReason={false}
                    allowB2C={allowB2C}
                  />
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </>
          <LoadingBox className={isLoading ? 'd-block' : 'd-none'} message="Processing" />
          {isError && (
            <ErrorBox
              className="d-block"
              message={getErrorMessagePayment({
                errorCode,
                declineCode,
                accountAlreadyExistsMessage,
                paddleLink: priceData.paddleLink
              })}
              onClick={
                isTrialAlreadyUsedError ? handleUpgradeClick : onTryAgain({ dispatch, accountAlreadyExistsMessage })
              }
              ctaLabel={isTrialAlreadyUsedError ? 'Upgrade' : undefined}
            />
          )}
        </div>

        <div className={styles.toc}>
          <p>No payments due now</p>
        </div>
      </div>
    </CustomModal>
  )
}

const mapStateToProps = (state) => ({
  declineCode: state.pricing.declineCode,
  errorCode: state.pricing.errorCode,
  transactionState: state.pricing.transactionState,
  accountAlreadyExistsMessage: state.pricing.accountAlreadyExistsMessage
})

export default connect(mapStateToProps)(withGoogleReCaptcha(FreeTrialPaymentModal))
